/** Anchors-view layout: the EXTERNAL activities (out-of-section work + finished
 *  work the section is tied to) as full-width rows in two direction groups,
 *  flanked by their in-section partner context rows. Pure transforms — no
 *  React — so the grouping/direction contracts unit-test directly.
 *
 *  Direction is the row's editing contract, not just a glyph — the table's
 *  single "In-section ties" column shows ONLY the row's direction-relevant
 *  side:
 *    "in"  — the external FEEDS the section. Its ties into the section are its
 *            SUCCESSORS; that's what the column shows ("+ Incoming successor").
 *    "out" — the external WAITS ON the section. Its ties are its
 *            PREDECESSORS ("+ Outgoing pred").
 *  Because each group can only stage ties in its own direction, an add can
 *  never flip a row into the other group.
 */
import type { DepGraph, DepGraphActivity } from "../../../models";
import { anchorKeyOf, byStartDate, isDriver, type RowLayoutEntry } from "./graphRows";

/** One external row's severed-tie facts: the deleted in-section partner names
 *  (rendered as ghost chips in the row's direction cell) and `disconnected` —
 *  NO live tie into the section survives, which earns the row the
 *  "⚠ tie severed" alarm until a tie is added back (or the delete undone). */
export interface SeveredRowInfo {
  partners: string[];
  disconnected: boolean;
}

/** Every outside tie in the graph: an edge with exactly one driver endpoint
 *  (other section / finished work) — PLUS ties a staged delete severed, which
 *  the live edge walk can't see (their in-section endpoint is gone). These are
 *  the section's anchors; each needs a review. */
export function collectAnchorKeys(graph: DepGraph): string[] {
  const out: string[] = [];
  for (const [succId, edges] of Object.entries(graph.edges)) {
    const succ = graph.activities[succId];
    if (!succ) continue;
    for (const e of edges) {
      const pred = graph.activities[e.pred_id];
      if (!pred) continue;
      const predDriver = isDriver(pred);
      const succDriver = isDriver(succ);
      if (predDriver === succDriver) continue;
      out.push(anchorKeyOf(e.pred_id, succId));
    }
  }
  for (const s of graph.severed_anchors ?? []) {
    // Same guard as buildAnchorLayout: a severed tie whose external node isn't
    // emitted has no row, so it must not count toward the badge either — an
    // uncounted tie beats one that can never be reviewed.
    if (!graph.activities[s.external_id]) continue;
    out.push(anchorKeyOf(s.pred_id, s.succ_id));
  }
  return out;
}

/** Anchors variant: the EXTERNAL activities themselves, each as a normal
 *  full-width row, in TWO groups — incoming (the external feeds section work;
 *  both-ways ties land here) and outgoing (the external waits on section
 *  work). A severed-only external groups by its severed ties' direction.
 *  Externals sort by date; each is flanked by its in-section partner copies
 *  (context rows: feeders directly above, fed work directly beneath) so every
 *  tie's arrow is one short hop. Each external row also carries its review
 *  keys (live + severed ties) and its severed-tie facts. */
export interface AnchorLayout {
  layout: RowLayoutEntry[];
  /** External id → every review key ("predId>succId") its row covers. */
  rowKeys: Map<string, string[]>;
  /** External id → its severed-tie facts (only externals with severed ties). */
  severedInfo: Map<string, SeveredRowInfo>;
  /** External id → which group it landed in. Drives the row's ←/→ arrow AND
   *  which side the single tie column shows + edits ("in" → successors,
   *  "out" → predecessors). */
  directions: Map<string, "in" | "out">;
}

export function buildAnchorLayout(graph: DepGraph): AnchorLayout {
  const keysByExternal = new Map<string, Set<string>>();
  const severedPartners = new Map<string, string[]>();
  // Live partners per external, deduped by partner id: the section work it
  // FEEDS (the external is pred) and the section work that FEEDS IT.
  const feeds = new Map<string, Map<string, DepGraphActivity>>();
  const fedBy = new Map<string, Map<string, DepGraphActivity>>();

  const addKey = (extId: string, key: string) => {
    const set = keysByExternal.get(extId);
    if (set) set.add(key);
    else keysByExternal.set(extId, new Set([key]));
  };
  const addPartner = (
    m: Map<string, Map<string, DepGraphActivity>>,
    extId: string,
    partner: DepGraphActivity,
  ) => {
    const inner = m.get(extId);
    if (inner) inner.set(partner.id, partner);
    else m.set(extId, new Map([[partner.id, partner]]));
  };

  for (const [succId, edges] of Object.entries(graph.edges)) {
    const succ = graph.activities[succId];
    if (!succ) continue;
    for (const e of edges) {
      const pred = graph.activities[e.pred_id];
      if (!pred) continue;
      const predDriver = isDriver(pred);
      if (predDriver === isDriver(succ)) continue;
      const ext = predDriver ? pred : succ;
      addKey(ext.id, anchorKeyOf(e.pred_id, succId));
      if (predDriver) addPartner(feeds, ext.id, succ);
      else addPartner(fedBy, ext.id, pred);
    }
  }

  const severedDirIn = new Set<string>();
  for (const s of graph.severed_anchors ?? []) {
    if (!graph.activities[s.external_id]) continue;
    addKey(s.external_id, anchorKeyOf(s.pred_id, s.succ_id));
    const name = s.deleted_name || s.deleted_task_code || s.deleted_id;
    const notes = severedPartners.get(s.external_id);
    if (!notes) severedPartners.set(s.external_id, [name]);
    else if (!notes.includes(name)) notes.push(name);
    if (s.direction === "in") severedDirIn.add(s.external_id);
  }

  const incoming: string[] = [];
  const outgoing: string[] = [];
  const directions = new Map<string, "in" | "out">();
  for (const id of Array.from(keysByExternal.keys())) {
    const hasLive = feeds.has(id) || fedBy.has(id);
    const isIncoming = hasLive ? feeds.has(id) : severedDirIn.has(id);
    directions.set(id, isIncoming ? "in" : "out");
    (isIncoming ? incoming : outgoing).push(id);
  }

  const layout: RowLayoutEntry[] = [];
  const pushGroup = (key: string, label: string, hint: string, ids: string[]) => {
    if (ids.length === 0) return;
    layout.push({ kind: "header", key, label, hint, count: ids.length, zone: null, rowIndex: 0 });
    const sorted = ids.map((id) => graph.activities[id]).sort(byStartDate);
    for (const ext of sorted) {
      const feeders = Array.from(fedBy.get(ext.id)?.values() ?? []).sort(byStartDate);
      for (const p of feeders) {
        layout.push({
          kind: "activity", key: `ap-${ext.id}-${p.id}`, activity: p,
          zoneKey: null, partnerOf: ext.id, partnerRole: "feeder",
          predDrivers: [], succDrivers: [], rowIndex: 0,
        });
      }
      layout.push({
        kind: "activity", key: ext.id, activity: ext, zoneKey: null,
        predDrivers: [], succDrivers: [], rowIndex: 0,
      });
      const fed = Array.from(feeds.get(ext.id)?.values() ?? []).sort(byStartDate);
      for (const p of fed) {
        layout.push({
          kind: "activity", key: `ab-${ext.id}-${p.id}`, activity: p,
          zoneKey: null, partnerOf: ext.id, partnerRole: "fed",
          predDrivers: [], succDrivers: [], rowIndex: 0,
        });
      }
    }
  };
  pushGroup(
    "h-anchors-in", "← Incoming · feeds work in this section",
    "ties here are its incoming successors", incoming,
  );
  pushGroup(
    "h-anchors-out", "→ Outgoing · waits on work in this section",
    "ties here are its outgoing preds", outgoing,
  );
  layout.forEach((e, i) => (e.rowIndex = i));

  const rowKeys = new Map<string, string[]>();
  for (const [id, set] of Array.from(keysByExternal)) rowKeys.set(id, Array.from(set));
  const severedInfo = new Map<string, SeveredRowInfo>();
  for (const [id, partners] of Array.from(severedPartners)) {
    severedInfo.set(id, {
      partners,
      disconnected: !(feeds.has(id) || fedBy.has(id)),
    });
  }
  return { layout, rowKeys, severedInfo, directions };
}
